import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRecurringBilling } from '@/hooks/useRecurringBilling';
import KPICard from './components/KPICard';
import CollectionAlerts from './components/CollectionAlerts';
import UpcomingRenewals from './components/UpcomingRenewals';
import PendingExpansionsCard from './components/PendingExpansionsCard';
import CashFlowChart from './components/CashFlowChart';
import BillingQueueCard from './components/BillingQueueCard';
import Badge from '@/components/base/Badge';

interface KpiState {
  mrr: number;
  cuentasPorCobrar: number;
  carteraVencida: number;
  licenciasActivas: number;
  licenciasLite: number;
  licenciasHosting: number;
  simsActivas: number;
  clientesActivos: number;
  clientesMorosos: number;
  cobranzaMes: number;
  porVencer: number;
}

interface Actividad {
  id: string;
  tipo: 'cliente' | 'factura' | 'pago' | 'suscripcion';
  accion: string;
  descripcion: string;
  fecha: string;
  monto?: number;
  icono: string;
  color: string;
}

function generateEmptyCashFlow() {
  const dias = [];
  const ahora = new Date();
  const diasEnMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0).getDate();
  for (let d = 1; d <= diasEnMes; d++) {
    dias.push({ dia: String(d), proyectado: 0, cobrado: 0 });
  }
  return dias;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpi, setKpi] = useState<KpiState>({
    mrr: 0, cuentasPorCobrar: 0, carteraVencida: 0,
    licenciasActivas: 0, licenciasLite: 0, licenciasHosting: 0,
    simsActivas: 0, clientesActivos: 0, clientesMorosos: 0,
    cobranzaMes: 0, porVencer: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intentosRef = useRef(0);
  const [cashFlowData, setCashFlowData] = useState<Array<{ dia: string; proyectado: number; cobrado: number }>>([]);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [colaFacturacion, setColaFacturacion] = useState<{ count: number; total: number }>({ count: 0, total: 0 });

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Motor de facturación recurrente: cuando se generan facturas automáticamente,
  // refresca los KPIs y métricas del dashboard.
  const billingResult = useRecurringBilling();
  useEffect(() => {
    if (billingResult && billingResult.created > 0) {
      fetchDashboardData();
    }
  }, [billingResult]);

  async function fetchDashboardData() {
    setError(null);
    try {
      const [
        clientsRes,
        invoicesRes,
        subsRes,
        paymentsRes,
      ] = await Promise.all([
        supabase.from('erp_clients').select('status'),
        supabase.from('erp_invoices').select('status, pending_balance, total_amount, due_date, folio, client_id, created_at').order('created_at', { ascending: false }).limit(20),
        supabase.from('erp_subscriptions').select('status, product_id, quantity, total_amount, client_id, created_at').eq('status', 'active'),
        supabase.from('erp_payments').select('amount, payment_date, invoice_id, payment_method, reference, created_at').order('created_at', { ascending: false }).limit(20),
      ]);

      if (clientsRes.error) throw clientsRes.error;
      if (invoicesRes.error) throw invoicesRes.error;
      if (subsRes.error) throw subsRes.error;
      if (paymentsRes.error) throw paymentsRes.error;

      const clients = clientsRes.data || [];
      const invoices = invoicesRes.data || [];
      const subscriptions = subsRes.data || [];
      const payments = paymentsRes.data || [];

      const clientesActivos = clients.filter(c => c.status === 'active').length;
      const clientesMorosos = clients.filter(c => c.status === 'delinquent').length;

      const cuentasPorCobrar = invoices
        .filter(i => i.status !== 'paid' && i.status !== 'draft')
        .reduce((s, i) => s + Number(i.pending_balance), 0);

      const carteraVencida = invoices
        .filter(i => i.status === 'overdue')
        .reduce((s, i) => s + Number(i.pending_balance), 0);

      const hoy = new Date();
      const en7Dias = new Date(hoy);
      en7Dias.setDate(en7Dias.getDate() + 7);
      const porVencer = invoices
        .filter(i => i.status === 'pending' && i.due_date && new Date(i.due_date) <= en7Dias && new Date(i.due_date) >= hoy)
        .reduce((s, i) => s + Number(i.total_amount), 0);

      const now = new Date();
      const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1);
      const cobranzaMes = payments
        .filter(p => new Date(p.payment_date) >= inicioMes)
        .reduce((s, p) => s + Number(p.amount), 0);

      const draftsRes = await supabase
        .from('erp_invoices')
        .select('id, total_amount')
        .eq('status', 'draft');
      const drafts = draftsRes.data || [];
      setColaFacturacion({
        count: drafts.length,
        total: drafts.reduce((s, d) => s + Number(d.total_amount), 0),
      });

      const productIds = [...new Set(subscriptions.map(s => s.product_id))];
      let mrr = 0;
      let licenciasLite = 0;
      let licenciasHosting = 0;
      let simsActivas = 0;

      if (productIds.length > 0) {
        const prodsRes = await supabase.from('erp_products').select('id, sku, category, unit_price').in('id', productIds);
        if (!prodsRes.error && prodsRes.data) {
          const prodMap = new Map(prodsRes.data.map(p => [p.id, p]));
          subscriptions.forEach(s => {
            const prod = prodMap.get(s.product_id);
            if (!prod) return;
            const qty = Number(s.quantity) || 1;
            if (prod.category === 'licencias' || prod.category === 'telecom' || prod.category === 'servicios') {
              mrr += Number(s.total_amount || prod.unit_price * qty);
            }
            if (prod.category === 'licencias') {
              if ((prod.sku || '').toUpperCase().includes('LITE')) licenciasLite += qty;
              else if ((prod.sku || '').toUpperCase().includes('HOST')) licenciasHosting += qty;
            }
            if (prod.category === 'telecom') simsActivas += qty;
          });
        }
      }

      setKpi({
        mrr,
        cuentasPorCobrar,
        carteraVencida,
        licenciasActivas: licenciasLite + licenciasHosting,
        licenciasLite,
        licenciasHosting,
        simsActivas,
        clientesActivos,
        clientesMorosos,
        cobranzaMes,
        porVencer,
      });

      const hoyProyeccion = new Date();
      const inicioMesStr = `${hoyProyeccion.getFullYear()}-${String(hoyProyeccion.getMonth() + 1).padStart(2, '0')}-01`;
      const finMesStr = `${hoyProyeccion.getFullYear()}-${String(hoyProyeccion.getMonth() + 1).padStart(2, '0')}-${String(new Date(hoyProyeccion.getFullYear(), hoyProyeccion.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      const projectionRes = await supabase
        .from('erp_invoices')
        .select('status, due_date, total_amount, pending_balance')
        .in('status', ['draft', 'pending'])
        .gte('due_date', inicioMesStr)
        .lte('due_date', finMesStr);
      const proyectadoPorDia = new Map<number, number>();
      (projectionRes.data || []).forEach(i => {
        const dia = new Date(i.due_date + 'T00:00:00').getDate();
        const monto = i.status === 'draft' ? Number(i.total_amount) : Number(i.pending_balance || i.total_amount);
        proyectadoPorDia.set(dia, (proyectadoPorDia.get(dia) || 0) + monto);
      });

      const emptyData = generateEmptyCashFlow();
      if (payments.length > 0) {
        const cobradoPorDia = new Map<number, number>();
        payments.forEach(p => {
          const dia = new Date(p.payment_date).getDate();
          cobradoPorDia.set(dia, (cobradoPorDia.get(dia) || 0) + Number(p.amount));
        });
        emptyData.forEach(d => {
          const diaNum = parseInt(d.dia);
          d.cobrado = cobradoPorDia.get(diaNum) || 0;
        });
      }
      emptyData.forEach(d => {
        d.proyectado = proyectadoPorDia.get(parseInt(d.dia)) || 0;
      });
      setCashFlowData(emptyData);

      const actividadesList: Actividad[] = [];

      const clientIdsInvoices = [...new Set(invoices.map(i => i.client_id).filter(Boolean))];
      const clientIdsPayments = [...new Set(payments.map(p => {
        const inv = invoices.find(i => i.id === p.invoice_id);
        return inv?.client_id;
      }).filter(Boolean))];
      const clientIdsSubs = [...new Set(subscriptions.map(s => s.client_id).filter(Boolean))];
      const allClientIds = [...new Set([...clientIdsInvoices, ...clientIdsPayments, ...clientIdsSubs])];

      let clientMap = new Map<number, string>();
      if (allClientIds.length > 0) {
        const clientNamesRes = await supabase.from('erp_clients').select('id, company_name').in('id', allClientIds);
        if (!clientNamesRes.error && clientNamesRes.data) {
          clientMap = new Map(clientNamesRes.data.map(c => [c.id, c.company_name]));
        }
      }

      invoices.slice(0, 8).forEach(i => {
        actividadesList.push({
          id: `inv-${i.id}`,
          tipo: 'factura',
          accion: 'Factura Creada',
          descripcion: `${i.folio} — ${clientMap.get(i.client_id) || 'Cliente'}`,
          fecha: i.created_at,
          monto: Number(i.total_amount),
          icono: 'ri-file-list-3-line',
          color: 'text-sky-600 bg-sky-50',
        });
      });

      payments.slice(0, 8).forEach(p => {
        const inv = invoices.find(i => i.id === p.invoice_id);
        const clientName = inv ? clientMap.get(inv.client_id) : null;
        actividadesList.push({
          id: `pay-${p.id}`,
          tipo: 'pago',
          accion: 'Pago Registrado',
          descripcion: `${clientName || 'Cliente'} — ${inv?.folio || 'Factura'} (${p.payment_method})`,
          fecha: p.created_at || p.payment_date,
          monto: Number(p.amount),
          icono: 'ri-money-dollar-circle-line',
          color: 'text-emerald-600 bg-emerald-50',
        });
      });

      subscriptions.filter(s => s.created_at).slice(0, 5).forEach(s => {
        actividadesList.push({
          id: `sub-${s.id}`,
          tipo: 'suscripcion',
          accion: 'Suscripción Creada',
          descripcion: `${clientMap.get(s.client_id) || 'Cliente'} — Qty: ${s.quantity || 1}`,
          fecha: s.created_at,
          monto: Number(s.total_amount),
          icono: 'ri-refresh-line',
          color: 'text-accent-600 bg-accent-50',
        });
      });

      const clientesRecientes = await supabase.from('erp_clients').select('id, company_name, created_at').order('created_at', { ascending: false }).limit(5);
      if (!clientesRecientes.error && clientesRecientes.data) {
        clientesRecientes.data.forEach(c => {
          actividadesList.push({
            id: `cli-${c.id}`,
            tipo: 'cliente',
            accion: 'Cliente Creado',
            descripcion: c.company_name,
            fecha: c.created_at,
            icono: 'ri-building-2-line',
            color: 'text-foreground-600 bg-background-100',
          });
        });
      }

      actividadesList.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
      setActividades(actividadesList.slice(0, 15));

    } catch (err) {
      console.error('Error fetching dashboard:', err);
      intentosRef.current += 1;
      if (intentosRef.current < 2) {
        setTimeout(() => {
          fetchDashboardData();
        }, 1500);
        return;
      }
      setError('No pudimos cargar los datos del Dashboard. Puede ser un problema temporal de conexión con el servidor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  const reintentar = () => {
    intentosRef.current = 0;
    setLoading(true);
    fetchDashboardData();
  };

  const nombreMes = new Intl.DateTimeFormat('es-MX', { timeZone: 'America/Mexico_City', month: 'long', year: 'numeric' }).format(new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
            <i className="ri-cloud-off-line text-red-500 text-xl"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900">No pudimos cargar el Dashboard</p>
            <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{error}</p>
          </div>
          <button
            onClick={reintentar}
            className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Dashboard</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Centro de Control Operativo y Financiero</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-download-2-line"></i>
            Exportar Reporte
          </button>
          <button
            onClick={() => navigate('/cobranza', { state: { openNewInvoice: true } })}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Nueva Factura
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="MRR Mensual"
          value={formatCurrency(kpi.mrr)}
          subtitle="Ingreso Recurrente Mensual"
          icon="ri-money-dollar-circle-line"
          iconBg="bg-primary-50"
          iconColor="text-primary-600"
        />
        <KPICard
          title="Cuentas por Cobrar"
          value={formatCurrency(kpi.cuentasPorCobrar)}
          subtitle={`${formatCurrency(kpi.carteraVencida)} vencidos`}
          icon="ri-bank-card-line"
          iconBg="bg-red-50"
          iconColor="text-red-500"
        >
          <div className="w-full h-1 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-red-500"
              style={{ width: `${kpi.cuentasPorCobrar > 0 ? (kpi.carteraVencida / kpi.cuentasPorCobrar) * 100 : 0}%` }}
            ></div>
          </div>
          <span className="text-[10px] text-red-500 font-medium">
            {kpi.cuentasPorCobrar > 0 ? ((kpi.carteraVencida / kpi.cuentasPorCobrar) * 100).toFixed(1) : '0.0'}% cartera vencida
          </span>
        </KPICard>
        <KPICard
          title="Licencias Activas"
          value={kpi.licenciasActivas.toLocaleString()}
          subtitle={`${kpi.licenciasLite} Lite · ${kpi.licenciasHosting} Hosting`}
          icon="ri-key-2-line"
          iconBg="bg-accent-50"
          iconColor="text-accent-600"
        />
        <KPICard
          title="SIMs Activas"
          value={kpi.simsActivas.toLocaleString()}
          subtitle="Suscripciones telecom activas"
          icon="ri-signal-tower-line"
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
      </div>

      <BillingQueueCard
        count={colaFacturacion.count}
        total={colaFacturacion.total}
        onNavigate={() => navigate('/cobranza', { state: { filterDraft: true } })}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <CollectionAlerts />
        </div>
        <div className="lg:col-span-2">
          <CashFlowChart data={cashFlowData} monthLabel={nombreMes} />
        </div>
      </div>

      <UpcomingRenewals />

      <PendingExpansionsCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-background-200 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-building-2-line text-foreground-500 text-lg"></i>
          </div>
          <div>
            <span className="text-xs text-foreground-500">Clientes Activos</span>
            <p className="text-lg font-bold text-foreground-900">{kpi.clientesActivos}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-500 text-lg"></i>
          </div>
          <div>
            <span className="text-xs text-foreground-500">Clientes Morosos</span>
            <p className="text-lg font-bold text-red-600">{kpi.clientesMorosos}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-double-line text-emerald-500 text-lg"></i>
          </div>
          <div>
            <span className="text-xs text-foreground-500">Cobranza del Mes</span>
            <p className="text-lg font-bold text-foreground-900">{formatCurrency(kpi.cobranzaMes)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-timer-line text-amber-500 text-lg"></i>
          </div>
          <div>
            <span className="text-xs text-foreground-500">Por Vencer (7 días)</span>
            <p className="text-lg font-bold text-foreground-900">{formatCurrency(kpi.porVencer)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">Últimas Acciones</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Trazabilidad de movimientos recientes en el ERP</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-[10px] text-foreground-500 font-medium">En tiempo real</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-background-100 bg-background-50">
                <th className="text-left px-5 py-2.5 font-semibold text-foreground-500">Acción</th>
                <th className="text-left px-5 py-2.5 font-semibold text-foreground-500">Detalle</th>
                <th className="text-right px-5 py-2.5 font-semibold text-foreground-500">Monto</th>
                <th className="text-right px-5 py-2.5 font-semibold text-foreground-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {actividades.map((a) => (
                <tr key={a.id} className="hover:bg-background-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${a.color.split(' ')[1]} flex-shrink-0`}>
                        <i className={`${a.icono} ${a.color.split(' ')[0]} text-xs`}></i>
                      </div>
                      <span className="font-medium text-foreground-800 whitespace-nowrap">{a.accion}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-foreground-600">{a.descripcion}</td>
                  <td className="px-5 py-3 text-right">
                    {a.monto !== undefined ? (
                      <span className={`font-semibold ${a.tipo === 'pago' ? 'text-emerald-600' : a.tipo === 'factura' ? 'text-foreground-800' : 'text-foreground-600'}`}>
                        {formatCurrency(a.monto)}
                      </span>
                    ) : (
                      <span className="text-foreground-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-foreground-500 whitespace-nowrap">
                    {new Date(a.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                    <span className="block text-[10px] text-foreground-400">
                      {new Date(a.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {actividades.length === 0 && (
          <div className="py-10 text-center">
            <i className="ri-history-line text-foreground-300 text-2xl"></i>
            <p className="text-sm text-foreground-500 mt-2">Sin actividad registrada aún</p>
            <p className="text-xs text-foreground-400 mt-1">Los movimientos aparecerán aquí conforme operes el sistema</p>
          </div>
        )}
      </div>
    </div>
  );
}